import { CacheManager } from '../utils/cacheManager';
import { normalizeArrayData } from '../utils/dataFormatHandler';

class ProductsService {
  constructor() {
    // Use proxy in development, direct URL in production
    this.baseUrl = process.env.NODE_ENV === 'development' ? '' : 'https://rainbow-sails.shop';
    this.cacheManager = new CacheManager(5 * 60 * 1000); // 5 minutes
    
    console.log('🚀 [SERVICE] ProductsService initialized');
    console.log('🌐 [SERVICE] Base URL:', this.baseUrl || 'Using proxy');
    console.log('🌐 [SERVICE] Environment:', process.env.NODE_ENV);
    console.log('⏰ [TIME]', new Date().toISOString());
  }

  async getProducts() {
    console.log('🚀 [FUNCTION] getProducts called');
    console.log('⏰ [TIME]', new Date().toISOString());
    console.log('🚀 [START] Fetching products from server...');
    
    // Clear cache to force fresh data fetch (since user re-uploaded products)
    this.clearCache();
    console.log('🗑️ [CACHE] Cache cleared - forcing fresh fetch');

    try {
      const url = `${this.baseUrl}/products/index.json`;
      console.log('📡 [API] Fetching products from:', url);
      console.log('🌐 [FETCH] Starting fetch request...');
      
      const response = await fetch(url);
      console.log('📡 [API] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 [API] Raw data received:', data);
      
      // Handle both array format and object format using shared utility
      const products = normalizeArrayData(data, 'products', 'products');
      
      console.log('📊 [API] Products data processed:', {
        productsCount: products.length,
        products: products.map(p => ({ id: p.id, name: p.name, folder: p.folder }))
      });
      
      this.cacheManager.set(products);
      console.log('💾 [CACHE] Products cached successfully');
      console.log('✅ [SUCCESS] Products loaded from server index:', products.length);
      return products;
    } catch (error) {
      console.error('❌ [ERROR] Error fetching products:', error);
      console.error('❌ [ERROR] Error details:', {
        message: error.message,
        stack: error.stack,
        url: `${this.baseUrl}/products/index.json`
      });
      return [];
    }
  }

  async getProduct(productId) {
    console.log('🚀 [FUNCTION] getProduct called with ID:', productId);
    console.log('⏰ [TIME]', new Date().toISOString());
    console.log('🚀 [START] Fetching individual product from server...');
    
    try {
      const url = `${this.baseUrl}/products/${productId}/product.json`;
      console.log('📡 [API] Fetching product from:', url);
      console.log('🌐 [FETCH] Starting fetch request for product:', productId);
      
      const response = await fetch(url);
      console.log('📡 [API] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
        productId
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const productData = await response.json();
      console.log('📊 [API] Product data received:', {
        productId: productData?.id,
        productName: productData?.name,
        hasImages: !!productData?.images,
        hasVariants: !!productData?.variants,
        hasPrintfulData: !!productData?.printfulData,
        published: productData?.published
      });
      
      console.log('✅ [SUCCESS] Product loaded successfully:', productId);
      return productData;
    } catch (error) {
      console.error('❌ [ERROR] Error fetching product:', error);
      console.error('❌ [ERROR] Error details:', {
        productId,
        message: error.message,
        stack: error.stack,
        url: `${this.baseUrl}/products/${productId}/product.json`
      });
      return null;
    }
  }

  clearCache() {
    console.log('🚀 [FUNCTION] clearCache called');
    console.log('⏰ [TIME]', new Date().toISOString());
    console.log('🗑️ [CACHE] Clearing products cache...');
    
    this.cacheManager.clear();
    
    console.log('✅ [SUCCESS] Products cache cleared successfully');
  }
}

const productsServiceInstance = new ProductsService();
export default productsServiceInstance;